import { useState } from "react";
import { JsonViewer } from "@textea/json-viewer";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshWarningButton } from "../base_components/sesh_warning_button";
import { SeshSelect } from "../base_components/sesh_select_input";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import ClipLoader from "react-spinners/ClipLoader";

export function AdminStripeManager() {
	const context = trpc.useContext();

	const active_coupon_query = trpc.admin.getActiveCoupon.useQuery();

	const active_coupon_mutation = trpc.admin.setActiveCoupon.useMutation({
		onSuccess: () => {
			context.admin.getActiveCoupon.invalidate();
		},
	});

	const setActiveCoupon = (coupon_id: string | null) =>
		active_coupon_mutation.mutate({ coupon_id });

	if (!active_coupon_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return (
		<div className="flex flex-col gap-10">
			<CheckoutEnabler />
			<PriceCacheClearer />
			<div className="flex flex-col gap-2">
				<div className="flex items-center">
					<div className="font-bold">Current Active Coupon</div>
					<SeshWarningButton
						className="ml-auto w-fit"
						onClick={() => setActiveCoupon(null)}
						is_loading={active_coupon_mutation.isLoading}
					>
						Clear Active Coupon
					</SeshWarningButton>
				</div>
				{active_coupon_query.data ? (
					<JsonViewer value={active_coupon_query.data} theme="light" />
				) : (
					<>None</>
				)}
			</div>
			<CouponSetter
				active_coupon_id={active_coupon_query.data?.id}
				setActiveCoupon={setActiveCoupon}
				is_loading={active_coupon_mutation.isLoading}
			/>
		</div>
	);
}

function PriceCacheClearer() {
	const mutation = trpc.admin.clearStripePriceCache.useMutation();

	return (
		<div className="flex items-center">
			<div className="font-bold">Price Cache</div>
			<SeshWarningButton
				className="ml-auto w-fit"
				onClick={() => mutation.mutate()}
				is_loading={mutation.isLoading}
			>
				Clear Price Cache
			</SeshWarningButton>
		</div>
	);
}

function CheckoutEnabler(props: {}) {
	const context = trpc.useContext();

	const checkout_disabled_query = trpc.admin.getCheckoutDisabled.useQuery();

	const mutation = trpc.admin.modifyCheckoutDisabled.useMutation({
		onSuccess: () => {
			context.admin.getCheckoutDisabled.invalidate();
		},
	});

	if (checkout_disabled_query.isLoading) {
		return <ClipLoader />;
	}

	return (
		<div>
			<div className="font-bold">Checkout Status</div>
			<div className="flex items-center gap-2">
				Current Setting:{" "}
				{checkout_disabled_query.data ? "Disabled" : "Enabled"}
				{checkout_disabled_query.data ? (
					<SeshPrimaryButton
						className="ml-auto w-fit"
						is_loading={mutation.isLoading}
						onClick={() =>
							mutation.mutate({
								is_disabled: !checkout_disabled_query.data,
							})
						}
					>
						{checkout_disabled_query.data ? "Enable" : "Disable"}
					</SeshPrimaryButton>
				) : (
					<SeshWarningButton
						className="ml-auto w-fit"
						is_loading={mutation.isLoading}
						onClick={() =>
							mutation.mutate({
								is_disabled: !checkout_disabled_query.data,
							})
						}
					>
						{checkout_disabled_query.data ? "Enable" : "Disable"}
					</SeshWarningButton>
				)}
			</div>
		</div>
	);
}

function CouponSetter(props: {
	active_coupon_id: string;
	setActiveCoupon: (coupon_id: string | null) => void;
	is_loading?: boolean;
}) {
	const all_coupons_query = trpc.admin.getCoupons.useQuery(undefined, {
		onSuccess: (data) => setOption(data[0]),
	});

	const all_coupons_query_data =
		all_coupons_query.data as RouterInferenceHelpers["admin"]["getCoupons"]["output"];

	const [option, setOption] = useState<
		RouterInferenceHelpers["admin"]["getCoupons"]["output"][number]
	>(all_coupons_query_data?.[0]);

	if (!all_coupons_query.isSuccess) {
		return <DashboardUtils.RenderLoadingSelect />;
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="font-bold">Set Coupon</div>
			<div className="flex gap-2">
				<div className="w-full">
					<SeshSelect
						options={all_coupons_query_data.map((val) => ({
							label:
								val.name +
								(props.active_coupon_id === val.id
									? " (active)"
									: "") +
								` (${val.id})`,
							value: val.id,
						}))}
						value={{ label: option.name, value: option.id }}
						onChange={(select_val) =>
							setOption(
								all_coupons_query_data.find(
									(val) => val.id === select_val.value
								)
							)
						}
					/>
				</div>
				<div className="ml-auto flex gap-2">
					<SeshPrimaryButton
						className="w-fit"
						onClick={() => props.setActiveCoupon(option.id)}
						is_loading={props.is_loading}
					>
						Set
					</SeshPrimaryButton>
				</div>
			</div>
			<JsonViewer value={option} theme="light" />
		</div>
	);
}
